/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console */

import React from 'react';
import PropTypes from 'prop-types';
import Screen from '../Screen';
import Dialog from '../../Dialog/Dialog';
import Button from '../../Button/Button';
import Entityview from '../../Entityview/Entityview';
import Entityselector from '../../Entityselector/Entityselector';
import Service from '../../Util/Service';
import Firebase from '../../Util/Firebase';
import $ from '../../Util/Util';
import './Entity.css';

/**
 * Represents the entity maintenance screen.
 */

export default class Entity extends Screen {

    /**
     * Properties.
     */

    static propTypes = {
        id:                     PropTypes.string,
        path:                   PropTypes.string,
        readonly:               PropTypes.bool,
        onBack:                 PropTypes.func.isRequired
    }

    /**
     * Default state.
     */

    state = {
        users:                  null,
        entitytypes:            null,
        deleteentity:           null,
        deletableentity:        null
    }

    _delete = (entity) => {
        let versions = Object.keys(entity.versions);
        let attributes = entity.versions[versions[versions.length - 1]].attributes;

        for (let i = 0; i < attributes.length; i++) {
            if (attributes[i].primary) {
                this.setState({ deleteentity: attributes[i].value, deletableentity: entity }, () => {
                    this.deleteEntityConfirmDialog.open();
                });
                break;
            }
        }
    }

    _cancelDelete = () => {
        this.deleteEntityConfirmDialog.close();
    }

    _deleteEntity = () => {
        this.deleteEntityConfirmDialog.close().then(() => {

            // Showing the progress indicator

            this.props.onLoading(true);

            // Deleting uploaded files of the entity

            let file = [];

            Object.keys(this.state.deletableentity.versions).forEach((key) => {
                this.state.deletableentity.versions[key].attributes.forEach((item) => {
                    if (item.type === 'file') {
                        if (item.multivalue) {
                            item.value.forEach((i) => {
                                if (i.url && file.indexOf(i.url) < 0) {
                                    file.push(i.url);
                                }    
                            });
                        } else {
                            if (item.value.url && file.indexOf(item.value.url) < 0) {
                                file.push(item.value.url);
                            }
                        }
                    } else if (item.type === 'picture' && item.value && file.indexOf(item.value) < 0) {
                        file.push(item.value);
                    }
                });
            });

            let deleteFile = (index, callback) => {
                if (index >= file.length) {
                    callback();
                } else {
                    Firebase.deleteFile(file[index]).then(() => {
                        deleteFile(index + 1, callback);
                    }).catch((error) => {
                        
                        // Showing error

                        $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });                        

                        // Callbacking

                        callback();
                    });
                }
            };

            deleteFile(0, () => {
                Service.deleteEntity(this.props.id, this.props.path).then(() => {

                    // Hiding the progress indicator

                    this.props.onLoading(false);

                    // Closing the entity

                    this.props.onBack();
                }).catch((error) => {

                    // Hiding the progress indicator

                    this.props.onLoading(false);

                    // Showing error

                    $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
                });
            });
        });
    }

    /**
     * Adds a new relation.
     */

    _addNewRelation = () => {
        this.addRelationDialog.open().then(() => {
            this.entityselector.load();
        });
    }

    /**
     * Closes the add new relation dialog.
     */

    _closeAddNewRelationDialog = () => {
        this.addRelationDialog.close();
    }

    /**
     * Event that fires when an entity selected at the add new relation dialog.
     */

    _onEntitySelected = () => {
        let relation = this.entityselector.getSelectedEntity();

        if (relation) {
            this.entityview.addRelation(relation);
        }
        
        this._closeAddNewRelationDialog();
    }

    _gotoEntity = (entity) => {
        let p = entity.path.split('/');
        this.entityview.loadEntity({ id: p[p.length - 1] }, this.props.readonly);
    }

    /** 
     * Event that fires when the screen mounted.
     */

    componentDidMount() {

        // Setting actionbar title

        this.props.onSetActionbarTitle(this.props.context.locale.entity.title);

        // Showing the progress indicator

        this.props.onLoading(true);

        // Loading the users

        Service.loadUser().then((users) => {
            this.setState({ users: users });

            // Loading the entity types

            Service.loadEntitytype().then((entitytypes) => {
                this.setState({ entitytypes: entitytypes });

                // Hiding the progress indicator

                this.props.onLoading(false);

                if (this.props.id === 'newentity') {
                    this.entityview.newEntity(this.props.path);
                }
            }).catch((error) => {

                // Hiding the progress indicator

                this.props.onLoading(false);

                // Showing error

                $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
            });
        }).catch((error) => {

            // Hiding the progress indicator

            this.props.onLoading(false);

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
        });
    }

    /** 
     * Entity maintenance screen renderer.
     */

    render() {
        return (
            <div className="entity">
                { this.state.users && this.state.entitytypes && <Entityview ref={c => {this.entityview = c;}} context={this.props.context} users={this.state.users} entitytypes={this.state.entitytypes} id={this.props.id || 'newentity'} readonly={this.props.readonly} onLoading={this.props.onLoading} onClose={this.props.onBack} onDelete={this._delete} onNewRelation={this._addNewRelation} onGotoRelation={this._gotoEntity}/> }

                {/* Delete entity confirm dialog */}

                <Dialog ref={c => {this.deleteEntityConfirmDialog = c;}} width={320} height={132} closetooltip={this.props.context.locale.dashboard.deleteentitydialog.close}>
                    <div className="entity-deletedialog-title">{this.props.context.locale.dashboard.deleteentitydialog.message}</div>
                    <div className="entity-deletedialog-name">{this.state.deleteentity}</div>

                    <div className="horizontal" style={{ marginTop: '28px' }}>
                        <div className="flex"></div>
                        <Button label={this.props.context.locale.dashboard.deleteentitydialog.buttons.cancel.label} tooltip={this.props.context.locale.dashboard.deleteentitydialog.buttons.cancel.tooltip} width={60} onTouch={this._cancelDelete} />
                        <Button label={this.props.context.locale.dashboard.deleteentitydialog.buttons.yes.label} tooltip={this.props.context.locale.dashboard.deleteentitydialog.buttons.yes.tooltip} width={60} leftgap={8} onTouch={this._deleteEntity} />
                    </div>
                </Dialog>

                {/* Add relation dialog */}

                <Dialog ref={c => {this.addRelationDialog = c;}} width={320} height={388} closetooltip={this.props.context.locale.dashboard.addrelationdialog.close}>
                    <div className="entity-entitydialog-title">{this.props.context.locale.dashboard.addrelationdialog.message}</div>
                    <Entityselector ref={c => {this.entityselector = c;}} context={this.props.context} height={280} onLoading={this.props.onLoading} />
                    <div className="horizontal">
                        <div className="flex"></div>
                        <Button label={this.props.context.locale.dashboard.addrelationdialog.buttons.cancel.label} tooltip={this.props.context.locale.dashboard.addrelationdialog.buttons.cancel.tooltip} width={60} topgap={10} onTouch={this._closeAddNewRelationDialog} />
                        <Button label={this.props.context.locale.dashboard.addrelationdialog.buttons.ok.label} tooltip={this.props.context.locale.dashboard.addrelationdialog.buttons.ok.tooltip} width={60} topgap={10} leftgap={8} onTouch={this._onEntitySelected} />
                    </div>
                </Dialog>                             
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Screens/Entity/Entity.js